<template>
  <v-card id="previous-trading-rules">
    <v-card-title class="i-card-title-div justify-center">
      <span class="g-bold g-heading-m">Supplier Trading Rules</span>
    </v-card-title>

    <v-card-text class="py-8 i-card-content-div">
      <v-row>
        <v-col class="pt-0 pb-2 pt-md-4" cols="12" md="6">
          <p class="g-bold g-text-color-black g-heading-s">
            Minimum Order value
          </p>
        </v-col>
        <v-col class="py-0" cols="12" md="6">
          <v-text-field
            background-color="#F0F0F0"
            dense
            disabled
            label="Minimum Order value"
            min="0"
            outlined
            :prefix="currencySymbol"
            type="number"
            v-model="rules.minimumOrderValue"
          ></v-text-field>
        </v-col>

        <v-col class="pt-0 pb-2 pt-md-4" cols="12" md="6">
          <p class="g-bold g-text-color-black g-heading-s">
            Free Freight value
          </p>
        </v-col>
        <v-col class="py-0" cols="12" md="6">
          <v-text-field
            background-color="#F0F0F0"
            class="pb-0"
            dense
            disabled
            hide-details="auto"
            min="0"
            outlined
            :prefix="currencySymbol"
            type="number"
            :label="
              rules.minimumOrderValueFreeFreightNotApplicable
                ? 'N/A'
                : 'Free Freight value'
            "
            :value="rules.minimumOrderValueFreeFreight"
          ></v-text-field>
          <v-checkbox
            dense
            disabled
            class="mt-0"
            label="I don't need a Free Freight value"
            v-model="rules.minimumOrderValueFreeFreightNotApplicable"
          />
        </v-col>
      </v-row>

      <v-divider class="mx-0 mt-3 mb-5" horizontal></v-divider>

      <v-row>
        <v-col cols="12">
          <p class="g-bold g-text-color-black g-heading-s">Geography</p>
          <legacy-radio-group
            disabled
            name="previous-trading-rules-geography"
            :default-col-props="{ cols: '12', md: '4', class: 'pb-0' }"
            :options="[
              { label: 'National', value: 'NATIONAL' },
              { label: 'State', value: 'STATE' },
              { label: 'Zone', value: 'ZONE' }
            ]"
            v-model="rules.type"
          />
        </v-col>

        <v-col cols="12">
          <p class="g-bold g-text-color-black g-heading-s">
            Additional Freight cost per carton
            <v-icon
              class="i-tooltip-color pb-1"
              small
              v-tooltip="{
                ref: 'additionalFreightCostPerCartonToolTipContent'
              }"
            >
              mdi-help-circle-outline
            </v-icon>
            <span ref="additionalFreightCostPerCartonToolTipContent">
              Applies if Retailer orders between Minimum Order & Free Freight
              values <br />
              Input value ex Tax - tax is applied in the Retailer shopping cart
            </span>
          </p>

          <v-simple-table class="mt-3" light dense>
            <template v-slot:default>
              <tbody>
                <tr :key="zone.zoneCode" v-for="zone in rules.zonePrices">
                  <td class="text-center p-table-prices-zone">
                    <span>{{ zone.zoneCode }}</span>
                  </td>
                  <td class="text-center p-table-prices-cost">
                    <v-text-field
                      background-color="#F0F0F0"
                      class="py-4"
                      dense
                      disabled
                      hide-details
                      label="Zone cost"
                      min="0"
                      outlined
                      :prefix="currencySymbol"
                      type="number"
                      :value="zone.freightPerCartonExGst"
                    ></v-text-field>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>

        <v-col cols="12">
          <p class="g-bold g-text-color-black g-heading-s">
            Additional Freight cost per pallet
            <v-icon
              class="i-tooltip-color pb-1"
              small
              v-tooltip="{
                ref: 'additionalFreightCostPerPalletToolTipContent'
              }"
            >
              mdi-help-circle-outline
            </v-icon>
            <span ref="additionalFreightCostPerPalletToolTipContent">
              Applies if Retailer orders between Minimum Order & Free Freight
              values <br />
              Input value ex Tax - tax is applied in the Retailer shopping cart
            </span>
          </p>

          <v-simple-table class="mt-3" light dense>
            <template v-slot:default>
              <tbody>
                <tr :key="zone.zoneCode" v-for="zone in rules.zonePrices">
                  <td class="text-center p-table-prices-zone">
                    <span>{{ zone.zoneCode }}</span>
                  </td>
                  <td class="text-center p-table-prices-cost">
                    <v-text-field
                      background-color="#F0F0F0"
                      class="py-4"
                      dense
                      disabled
                      hide-details
                      label="Zone cost"
                      min="0"
                      outlined
                      :prefix="currencySymbol"
                      type="number"
                      :value="zone.freightPerPalletExGst"
                    ></v-text-field>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="i-dialog-actions-div pt-6 justify-end">
      <v-btn rounded text class="i-btn-text-secondary" @click="$emit('close')"
        >Close</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import LegacyRadioGroup from '@/components/LegacyRadioGroup.vue'

export default {
  components: {
    LegacyRadioGroup
  },

  props: {
    rules: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('tenant', ['tenantCurrency', 'tenantLocale']),
    currencySymbol() {
      return (
        new Intl.NumberFormat(this.tenantLocale, {
          style: 'currency',
          currency: this.tenantCurrency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value || '$'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#previous-trading-rules {
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  box-shadow: none;
  background: $bg-secondary-color;
  .v-card__text {
    flex-grow: 1;
    overflow: auto;
  }
  .p-table-prices-zone {
    width: 40%;
  }
}

@media only screen and (min-width: 960px) {
  #previous-trading-rules {
    .p-table-prices-zone {
      width: 50%;
    }
  }
}
</style>
